import React, { useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    Stack,
    Grid,
    Button,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { Link as RouterLink } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

const HelpPage: React.FC = () => {
    useSEO({
        title: 'راهنمای چیستا',
        description: 'پاسخ سوالات متداول درباره ثبت کسب‌وکار، جستجو و استفاده از چیستا.',
        canonical: '/cheesta-help',
    });

    // Router links with a #hash (e.g. /cheesta-help#autoscan) do not scroll on their own.
    useEffect(() => {
        const target = window.location.hash.replace('#', '');
        if (target === 'autoscan' || target === 'featured') {
            setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
        }
    }, []);

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: { xs: 6, md: 10 } }}>
            {/* Hero Section - same style as the Services page: brand colour,
                soft decorative circles, title with a one-line subtitle. */}
            <Box
                sx={{
                    width: '100%',
                    minHeight: { xs: 200, md: 260 },
                    position: 'relative',
                    overflow: 'hidden',
                    mb: -4,
                    pt: { xs: 3, md: 4 },
                    pb: { xs: 7, md: 8 },
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'white',
                    background: 'linear-gradient(135deg, #2854a1 0%, #1e4488 55%, #1a3a7a 100%)'
                }}
            >
                {/* Decorative elements */}
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
                <Box sx={{ position: 'absolute', bottom: -110, left: -90, width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
                <Box sx={{ position: 'absolute', top: 30, left: '18%', width: 70, height: 70, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.10)', display: { xs: 'none', md: 'block' } }} />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: { xs: 48, md: 56 },
                        height: { xs: 48, md: 56 },
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5
                    }}
                >
                    <HelpOutlineIcon sx={{ fontSize: { xs: 26, md: 30 } }} />
                </Box>
                <Typography
                    variant="h1"
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        fontWeight: 900,
                        fontSize: { xs: '1.7rem', md: '2.4rem' },
                        textShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        mb: 1
                    }}
                >
                    راهنمای چیستا
                </Typography>
                <Typography
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        opacity: 0.9,
                        direction: 'rtl',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        lineHeight: 1.8,
                        maxWidth: 560
                    }}
                >
                    راهنمای گام‌به‌گام ثبت کسب و کار و پاسخ پرسش‌های پرتکرار
                </Typography>
            </Box>

            <Container maxWidth="md">
                <Paper elevation={4} sx={{ borderRadius: 6, overflow: 'hidden', position: 'relative', zIndex: 2, p: { xs: 3, md: 8 } }}>
                    {/* Intro Section */}
                    <Box sx={{ mb: 6, direction: 'rtl', textAlign: 'right' }}>
                        <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.8, color: '#4a5568', mb: 4 }}>
                            لطفا قبل از درج ثبت کسب و کار یا آگهی <Button component={RouterLink} to="/cheesta-terms" sx={{ fontWeight: 700, color: '#2854a1', px: 0.5, fontSize: 'inherit' }}>قوانین و مقررات چیستا</Button> را بررسی نمایید.
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.8, color: '#4a5568' }}>
                            جهت ثبت کسب و کار باید با اکانت جیمیل و یا عضویت در سایت وارد شوید و کسب و کار خود را به رایگان ثبت نمایید. پس از ثبت و تایید توسط مدیر سایت، کسب و کار شما در جستجو نمایش داده خواهد شد.
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 6 }} />

                    {/* Features Grid */}
                    <Grid container spacing={4} sx={{ direction: 'rtl', mb: 6 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ p: 1, bgcolor: 'rgba(40, 84, 161, 0.1)', borderRadius: 2, color: '#2854a1', ml: 2 }}>
                                    <AssignmentIcon />
                                </Box>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, direction: 'rtl' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748', textAlign: 'right' }}>
                                            تخفیف‌ها و خدمات ویژه
                                        </Typography>
                                        <Box 
                                            sx={{ 
                                                px: 1, 
                                                py: 0.2, 
                                                bgcolor: '#fef2f2', 
                                                color: '#dc2626', 
                                                borderRadius: 1, 
                                                fontSize: '0.7rem', 
                                                fontWeight: 800,
                                                border: '1px solid #fecaca'
                                            }}
                                        >
                                            بزودی
                                        </Box>
                                    </Box>
                                    <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                        در فازهای بعدی، امکان ثبت تخفیف‌های ویژه و پیشنهادات محدود زمانی فراهم خواهد شد تا بتوانید مشتریان بیشتری را به سمت بیزینس خود جذب کنید.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ p: 1, bgcolor: 'rgba(40, 84, 161, 0.1)', borderRadius: 2, color: '#2854a1', ml: 2 }}>
                                    <HelpOutlineIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748', mb: 1, textAlign: 'right' }}>
                                        اطلاعات دقیق و کامل
                                    </Typography>
                                    <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'right' }}>
                                        به هنگام ثبت کسب و کار سعی کنید که آدرس، شماره تلفن، وب سایت شرکت، ساعات کاری و توضیحات کامل وارد نمایید. هر چه اطلاعات دقیق‌تر باشد، بازدیدکننده بیشتری خواهید داشت.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>

                    {/* Website auto-scan guide */}
                    <Box id="autoscan" sx={{ bgcolor: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.25)', p: { xs: 3, md: 4 }, borderRadius: 4, mb: 6, direction: 'rtl', textAlign: 'right', scrollMarginTop: '90px' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#047857', mb: 1.5 }}>
                            ثبت سریع کسب و کار با اسکن خودکار وب‌سایت
                        </Typography>
                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.8, mb: 2 }}>
                            اگر کسب و کار شما وب‌سایت دارد، چیستا می‌تواند اطلاعات آن را به‌صورت خودکار بخواند و یک پیش‌نویس آگهی بسازد. مراحل کار:
                        </Typography>
                        <Box component="ol" sx={{ m: 0, pr: 3, pl: 0, color: '#4a5568', fontSize: '0.95rem', lineHeight: 2 }}>
                            <li>وارد حساب کاربری خود شوید (در صورت نداشتن حساب ثبت‌نام کنید و ایمیل خود را تأیید نمایید).</li>
                            <li>از منوی کاربری (کنار نام شما در بالای صفحه) گزینه «اسکن خودکار وب‌سایت» را باز کنید.</li>
                            <li>آدرس وب‌سایت کسب و کار و یک ایمیل برای تأیید مالکیت را وارد کنید. این ایمیل باید روی دامنه وب‌سایت شما باشد (مثلاً info@yourbusiness.com.au) یا همان ایمیل در خود وب‌سایت (صفحه اصلی، پایین صفحه یا بخش تماس با ما) نوشته شده باشد. ایمیل‌های جیمیل هم در صورتی که در وب‌سایت درج شده باشند قابل قبول است.</li>
                            <li>کد ۶ رقمی ارسال‌شده به آن ایمیل را وارد کنید. کد ۳۰ دقیقه معتبر است.</li>
                            <li>تأیید کنید که کسب و کار شما ایرانی/فارسی‌زبان است و «شروع اسکن» را بزنید. نام دامنه تأییدشده قابل تغییر نیست.</li>
                            <li>اسکن در پس‌زمینه انجام می‌شود و ممکن است چند دقیقه طول بکشد؛ می‌توانید صفحه را ببندید.</li>
                            <li>از منوی کاربری، بخش «وب‌سایت‌های اسکن‌شده» را باز کنید و روی «باز کردن پیش‌نویس» بزنید. اطلاعات را بررسی و در صورت نیاز اصلاح کنید و سپس آگهی را ثبت نمایید.</li>
                        </Box>
                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.8, mt: 2.5 }}>
                            <strong>نکات مهم:</strong> فقط وب‌سایت کسب و کار خودتان را اسکن کنید. اطلاعات خودکار ممکن است ناقص یا نادرست باشد، بنابراین پیش از ثبت حتماً آن را بررسی کنید. پیش‌نویس‌ها حدود ۶۰ روز نگهداری می‌شوند. اگر اسکن موفق نشد، می‌توانید دوباره تلاش کنید یا آگهی را به‌صورت دستی ثبت نمایید.
                        </Typography>
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to="/user/website-scan"
                            sx={{ mt: 3, bgcolor: '#2854a1', px: 4, py: 1.2, borderRadius: 3, fontWeight: 700, '&:hover': { bgcolor: '#1a3a7a' } }}
                        >
                            شروع اسکن خودکار وب‌سایت
                        </Button>
                    </Box>

                    {/* Featured listings guide */}
                    <Box id="featured" sx={{ bgcolor: '#fffaf0', border: '1px solid #f6e0b5', p: { xs: 3, md: 4 }, borderRadius: 4, mb: 6, direction: 'rtl', textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#975a16', mb: 1.5 }}>
                            آگهی ویژه و برگزیده چیست؟
                        </Typography>
                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.8, mb: 2 }}>
                            ثبت کسب و کار در چیستا رایگان است (طرح Basic). برای دیده شدن بیشتر دو طرح داریم:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pr: 3, pl: 0, color: '#4a5568', fontSize: '0.95rem', lineHeight: 2 }}>
                            <li><strong>ویژه (Featured)</strong> – نمایش در بالای نتایج جستجو، دسته‌بندی‌ها و شهرها با نشان طلایی «ویژه».</li>
                            <li><strong>برگزیده (Premium)</strong> – همه امکانات ویژه، به‌علاوه جایگاه در صفحه اصلی (تعداد محدود)، نشان بنفش «برگزیده» بالاتر از آگهی‌های ویژه، تأیید سریع‌تر آگهی و ویرایش‌ها، یک <strong>تست نفوذ حرفه‌ای وب‌سایت</strong> (به ارزش ۴۹۹ دلار) و یک <strong>بررسی SEO وب‌سایت</strong> (به ارزش ۲۹۹ دلار). طرح برگزیده فقط سالانه عرضه می‌شود.</li>
                            <li>طرح ویژه را می‌توانید یک‌ماهه یا سالانه بخرید؛ طرح‌های سالانه به قیمت ۱۰ ماه هستند (۲ ماه رایگان). پرداخت یک‌باره است و تمدید خودکار ندارد.</li>
                            <li>هنگام ثبت کسب و کار، در انتهای فرم می‌توانید طرح را انتخاب کنید؛ یا بعداً از «پنل کاربری» دکمه Promote کنار آگهی را بزنید.</li>
                            <li>پرداخت از طریق درگاه امن Stripe انجام می‌شود؛ اطلاعات کارت شما به چیستا نمی‌رسد. رسید را Stripe و تأیید پرداخت را چیستا به ایمیل شما می‌فرستند.</li>
                            <li>اگر آگهی هنوز در انتظار تأیید باشد، طرح از روز تأیید شروع می‌شود. خرید دوباره در زمان فعال بودن طرح، به انتهای دوره فعلی اضافه می‌شود.</li>
                            <li>تاریخچه همه پرداخت‌ها و دوره‌ها در منوی کاربری، بخش «تبلیغات و پرداخت‌ها» است.</li>
                        </Box>
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to="/pricing"
                            sx={{ mt: 3, bgcolor: '#b7791f', px: 4, py: 1.2, borderRadius: 3, fontWeight: 700, '&:hover': { bgcolor: '#975a16' } }}
                        >
                            مشاهده تعرفه‌ها
                        </Button>
                    </Box>

                    {/* Multimedia Section */}
                    <Box sx={{ bgcolor: 'rgba(40, 84, 161, 0.03)', p: 4, borderRadius: 4, mb: 6, direction: 'rtl', textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2854a1', mb: 2 }}>
                            استفاده از تصاویر
                        </Typography>
                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7 }}>
                            وارد کردن عکس کمک زیادی به معرفی بهتر کسب و کار شما خواهد کرد. سعی کنید عکس با کیفیت خوب و واضح جهت آشنایی بهتر بازدید کنندگان ارسال نمایید. توضیحات کسب و کار را میتوانید به صورت فارسی و یا انگلیسی وارد نمایید.
                        </Typography>
                    </Box>

                    {/* Language Section */}
                    <Box sx={{ mb: 6, direction: 'rtl', textAlign: 'right' }}>
                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.7 }}>
                            ما سعی کردیم تا وب سایت را تا حد زیادی به فارسی ایجاد کنیم تا زبان شیرین فارسی را پاس بداریم. در صورتی که کیبورد فارسی ندارید میتوانید از <a href="https://www.branah.com/farsi" target="_blank" rel="noopener noreferrer" style={{ color: '#2854a1', fontWeight: 700, textDecoration: 'none' }}>این لینک</a> جهت تایپ فارسی استفاده نمایید.
                        </Typography>
                    </Box>

                    {/* Contact CTA */}
                    <Box sx={{ textAlign: 'center', p: 4, border: '2px dashed rgba(40, 84, 161, 0.2)', borderRadius: 4 }}>
                        <ContactSupportIcon sx={{ fontSize: '3rem', color: '#2854a1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            مشکلی در ثبت دارید؟
                        </Typography>
                        <Typography sx={{ color: '#4a5568', mb: 3 }}>
                            در صورتی که به هر دلیلی در ثبت کسب و کار خود مشکل دارید لطفا با ما تماس بگیرید.
                        </Typography>
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to="/contact"
                            sx={{
                                bgcolor: '#2854a1',
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                fontWeight: 700,
                                '&:hover': { bgcolor: '#1a3a7a' }
                            }}
                        >
                            تماس با ما
                        </Button>
                    </Box>

                    {/* IT consulting CTA */}
                    <Box sx={{ textAlign: 'center', p: 4, mt: 4, bgcolor: 'rgba(40, 84, 161, 0.05)', borderRadius: 4, direction: 'rtl' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            برای کسب و کار خود به مشاوره آی‌تی نیاز دارید؟
                        </Typography>
                        <Typography sx={{ color: '#4a5568', fontSize: '0.95rem', mb: 3 }}>
                            تیم فنی چیستا در طراحی وب‌سایت، شبکه، امنیت و خدمات فناوری اطلاعات کنار شماست. لطفا با ما تماس بگیرید.
                        </Typography>
                        <Button
                            variant="outlined"
                            component={RouterLink}
                            to="/hire-us"
                            sx={{
                                borderColor: '#2854a1',
                                color: '#2854a1',
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                fontWeight: 700,
                                textDecoration: 'none !important',
                                '&:hover': { bgcolor: 'rgba(40, 84, 161, 0.08)', borderColor: '#1a3a7a' }
                            }}
                        >
                            تماس با تیم فنی چیستا
                        </Button>
                    </Box>

                    {/* Footer Quote */}
                    <Box sx={{ mt: 8, textAlign: 'center' }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#2d3748',
                                fontStyle: 'italic',
                                fontWeight: 600,
                                mb: 2,
                                direction: 'rtl'
                            }}
                        >
                            “United We Stand, Divided We Fall”
                        </Typography>
                        <Typography sx={{ color: '#718096', fontSize: '0.95rem', lineHeight: 1.7, direction: 'rtl' }}>
                            چیستا مرجع کسب و کارهای ایرانیان مقیم استرالیا تلاش میکند تا هموطنان عزیز همدیگر را سریع تر و راحت تر بیایند.
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default HelpPage;
